import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { fetchUserOrders } from "../../../features/orderSlice";
import config from "../../../config";
import { FiPackage, FiBookOpen } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const statusColors = {
  PENDING: "bg-yellow-100 text-yellow-800",
  BOOK_ORDERED: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-green-100 text-green-800",
  CANCELLED: "bg-red-100 text-red-800",
  ACTIVE: "bg-orange-100 text-orange-800",
};

const formatDate = (dateArray) => {
  if (!dateArray || !Array.isArray(dateArray)) return "N/A";
  const [year, month, day, hour, minute, second] = dateArray;
  return new Date(
    year,
    month - 1,
    day,
    hour,
    minute,
    second
  ).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const BookWithMe = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { userOrders, status } = useSelector((state) => state.orders);
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserOrders(userId));
    }
  }, [userId, dispatch]);

  const filteredOrders = userOrders.filter(
    (order) => order.deliveryStatus === "BOOK_WITH_ME"
  );

  if (status === "loading") {
    return (
      <div className="p-6 bg-primary-shade1 min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-500">Loading books...</p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-br from-primary-shade1/30 to-primary-shade2/10 min-h-screen">
      <motion.h1
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="text-4xl font-bold bg-gradient-to-r from-primary-shade4 to-primary-shade5 bg-clip-text text-transparent mb-8 text-center font-nunito"
      >
        Books With Me
      </motion.h1>

      <AnimatePresence mode="wait">
        {status === "loading" ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center h-96"
          >
            <div className="animate-bounce flex flex-col items-center gap-4">
              <FiBookOpen className="w-12 h-12 text-primary-shade3 animate-pulse" />
              <p className="text-lg text-primary-shade4 font-nunito">
                Curating your books...
              </p>
            </div>
          </motion.div>
        ) : filteredOrders.length === 0 ? (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center justify-center h-[60vh] text-center"
          >
            <div className="relative max-w-md px-8 py-12 bg-white rounded-3xl shadow-neumorphic">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                <div className="p-4 bg-primary-shade1 rounded-full shadow-lg">
                  <FiBookOpen className="w-8 h-8 text-primary-shade5" />
                </div>
              </div>
              <h2 className="text-2xl font-bold font-nunito mb-4 bg-gradient-to-r from-primary-shade4 to-primary-shade5 bg-clip-text text-transparent">
                Your Bookshelf Awaits!
              </h2>
              <p className="text-gray-600 mb-8 font-nunito">
                Discover amazing books waiting to be read. Start your literary journey
                with our vast collection of curated titles.
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/explore-books"
                  className="inline-block bg-gradient-to-r from-primary-shade3 to-primary-shade5 text-white px-8 py-3 rounded-full font-nunito font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Explore Books
                </Link>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto"
          >
            {filteredOrders.reverse().map((order, index) => {
              const book = order.book ?? {};
              const author = book?.author ?? {};

              return (
                <motion.div
                  key={order.orderId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative bg-white rounded-2xl shadow-xl hover:shadow-3d transition-all duration-300 overflow-hidden"
                >
                  {book?.bookImages?.length > 0 ? (
                    <div className="relative h-48 overflow-hidden  onClick={() => navigate(`/book/${book.bookId}`)}">
                      <img
                        src={`${config.url}${book.bookImages[0]}`}
                        alt={book.bookName}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    </div>
                  ) : (
                    <div className="h-48 bg-primary-shade1 flex items-center justify-center">
                      <FiBookOpen className="w-12 h-12 text-primary-shade3" />
                    </div>
                  )}

                  <div className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-gray-800 truncate mb-1 font-nunito">
                          {book?.bookName || "Mystery Book"}
                        </h2>
                        <p className="text-sm text-gray-500 truncate font-nunito">
                          by {author?.authorName || "Anonymous Author"}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs rounded-full ${
                          statusColors[order.orderStatus]
                        } font-medium`}
                      >
                        {order.orderStatus}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-primary-shade1/30 rounded-xl">
                      <FiPackage className="w-5 h-5 text-primary-shade4 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-700 truncate font-nunito">
                          {order.deliveryStatus.replace(/_/g, " ")}
                        </p>
                        <p className="text-xs text-gray-500 font-nunito">
                          {formatDate(order.creationDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookWithMe;
